import { Component, Inject, OnDestroy, OnInit } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import {
    MatDialogModule,
    MatDialogRef,
    MAT_DIALOG_DATA,
} from "@angular/material/dialog";
import { MatButtonModule } from "@angular/material/button";
import { MatIconModule } from "@angular/material/icon";
import { MatProgressBarModule } from "@angular/material/progress-bar";
import { MatTooltipModule } from "@angular/material/tooltip";
import { MatDividerModule } from "@angular/material/divider";
import { MatChipsModule } from "@angular/material/chips";
import { SiteService } from "../../../core/services/site.service";
import { GroupService } from "../../../core/services/group.service";
import { CurrentSiteService } from "../../../core/services/current-site.service";
import { environment } from "../../../../environments/environment";

// ─── Constants ────────────────────────────────────────────────────────────────

const MAX_FILE_SIZE_BYTES = 10 * 1024 * 1024;

// site_id and user_type are injected automatically from the admin's current context
// (same as single-user creation) — they are NOT user-mappable columns.
const TARGET_FIELDS = [
    { key: "name",            displayName: "First Name",       required: true  },
    { key: "surname",         displayName: "Last Name",        required: true  },
    { key: "identity_number", displayName: "ID / Passport",   required: true  },
    { key: "msisdn",          displayName: "Phone Number",     required: true  },
    { key: "email",           displayName: "Email",            required: false },
    { key: "gender",          displayName: "Gender",           required: false },
    { key: "job_description", displayName: "Job Description",  required: false },
];

const REQUIRED_KEYS = TARGET_FIELDS.filter(f => f.required).map(f => f.key);

// ─── Interfaces ───────────────────────────────────────────────────────────────

interface Suggestion {
    target_field: string;
    source_column: string;
    confidence: number;
    reasoning: string;
    is_required: boolean;
}

interface AnalyzeResponse {
    session_id: string;
    expires_at: string;
    headers: string[];
    suggestions: Suggestion[];
    preview_rows: string[][];
    ai_available: boolean;
    warnings: string[];
}

interface ImportResponse {
    total_processed: number;
    total_registered: number;
    total_skipped: number;
    total_failed: number;
    failed_records?: FailedRecord[];
    error_report_csv?: string;
}

interface FailedRecord {
    row: number;
    identity_number: string;
    email: string;
    errors: string[];
}

interface AuditRecord {
    id: number;
    original_headers: string[];
    confirmed_mappings: { source_column: string; target_field: string }[];
    ai_available: boolean;
    total_processed: number;
    total_registered: number;
    total_skipped: number;
    total_failed: number;
    created_at: string;
}

type DialogPhase = "upload" | "analyzing" | "mapping" | "importing" | "results" | "audit";

// ─── Component ────────────────────────────────────────────────────────────────

@Component({
    selector: "app-bulk-user-registration-dialog",
    standalone: true,
    imports: [
        MatDialogModule,
        MatButtonModule,
        MatIconModule,
        MatProgressBarModule,
        MatTooltipModule,
        MatDividerModule,
        MatChipsModule,
    ],
    template: `
        <!-- ═══ TITLE ═══ -->
        <h2 mat-dialog-title class="dialog-title">
            <mat-icon class="title-icon">group_add</mat-icon>
            Bulk User Registration
            <button
                class="close-btn"
                [disabled]="phase === 'importing' || phase === 'analyzing'"
                [matTooltip]="phase === 'importing' ? 'Import in progress…' : 'Close'"
                (click)="dialogRef.close(false)"
            >
                <mat-icon>close</mat-icon>
            </button>
        </h2>

        <mat-dialog-content class="dialog-content">

            <!-- ══════════════════════════════════════════════════════════════
                 PHASE: upload
                 ════════════════════════════════════════════════════════════ -->
            @if (phase === 'upload') {
                <p class="phase-description">
                    Upload any CSV or Excel (.xlsx) file — the system will automatically
                    detect columns and suggest mappings using AI.
                </p>

                <div class="template-bar">
                    <mat-icon class="template-icon">download</mat-icon>
                    <span>Need a template?</span>
                    <button mat-stroked-button color="primary" (click)="downloadTemplate()" class="template-btn">
                        Download CSV Template
                    </button>
                    <button mat-stroked-button class="audit-btn" (click)="openAudit()">
                        <mat-icon>history</mat-icon> Import History
                    </button>
                </div>

                <div
                    class="drop-zone"
                    [class.drag-over]="isDragOver"
                    [class.has-file]="selectedFile"
                    (dragover)="onDragOver($event)"
                    (dragleave)="onDragLeave($event)"
                    (drop)="onDrop($event)"
                    (click)="fileInput.click()"
                >
                    <input #fileInput type="file"
                        accept=".csv,text/csv,.xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                        style="display:none" (change)="onFileSelected($event)" />
                    @if (!selectedFile) {
                        <mat-icon class="drop-icon">upload_file</mat-icon>
                    } @else {
                        <mat-icon class="drop-icon file-ready">check_circle</mat-icon>
                    }
                    <div class="drop-label">
                        @if (!selectedFile) {
                            <span>Drag & drop your CSV or Excel file here, or <strong>click to browse</strong></span>
                        } @else {
                            <span class="file-name-display">
                                {{ selectedFile.name }}
                                <span class="file-size">({{ formatFileSize(selectedFile.size) }})</span>
                            </span>
                        }
                    </div>
                    <div class="drop-hint">Accepted: .csv, .xlsx &nbsp;|&nbsp; Max size: 10 MB</div>
                </div>

                <!-- Site / group assignment (optional) -->
                <div class="assign-section">
                    <p class="assign-label">
                        <mat-icon class="assign-icon">business</mat-icon>
                        Assign imported users to a site and group <span class="optional-tag">optional</span>
                    </p>
                    <div class="assign-row">
                        <div class="assign-field">
                            <label class="field-label">Site</label>
                            <div class="select-wrapper" [class.select-mapped]="selectedSiteId">
                                <select class="custom-select" (change)="onSiteChange($event)" [disabled]="sitesLoading">
                                    <option value="">— No site —</option>
                                    @for (s of sites; track s.id) {
                                        <option [value]="s.id" [selected]="selectedSiteId === s.id">{{ s.name }}</option>
                                    }
                                </select>
                                <div class="select-arrow">
                                    <svg width="12" height="8" viewBox="0 0 12 8" fill="none">
                                        <path d="M1 1L6 6L11 1" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                                    </svg>
                                </div>
                            </div>
                        </div>
                        <div class="assign-field">
                            <label class="field-label">Group</label>
                            <div class="select-wrapper" [class.select-mapped]="selectedGroupId">
                                <select class="custom-select" (change)="onGroupChange($event)"
                                    [disabled]="!selectedSiteId || groupsLoading">
                                    <option value="">— No group —</option>
                                    @for (g of groups; track g.id) {
                                        <option [value]="g.id" [selected]="selectedGroupId === g.id">{{ g.name }}</option>
                                    }
                                </select>
                                <div class="select-arrow">
                                    <svg width="12" height="8" viewBox="0 0 12 8" fill="none">
                                        <path d="M1 1L6 6L11 1" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                                    </svg>
                                </div>
                            </div>
                            @if (!selectedSiteId) {
                                <span class="field-hint">Select a site first</span>
                            }
                        </div>
                    </div>
                    <p class="assign-note">
                        <mat-icon class="note-icon">info</mat-icon>
                        All imported users will be created with the default user role.
                        Site and group can also be assigned later.
                    </p>
                </div>

                @if (uploadError) {
                    <div class="alert alert-error">
                        <mat-icon>error_outline</mat-icon> {{ uploadError }}
                    </div>
                }
            }

            <!-- ══════════════════════════════════════════════════════════════
                 PHASE: analyzing
                 ════════════════════════════════════════════════════════════ -->
            @if (phase === 'analyzing') {
                <div class="center-status">
                    <mat-progress-bar mode="indeterminate"></mat-progress-bar>
                    <p class="status-label">Uploading file and detecting column mappings…</p>
                </div>
            }

            <!-- ══════════════════════════════════════════════════════════════
                 PHASE: mapping
                 ════════════════════════════════════════════════════════════ -->
            @if (phase === 'mapping') {

                <!-- Session / AI status bar -->
                <div class="status-bar" [class.status-bar-warning]="sessionExpiringSoon" [class.status-bar-error]="sessionExpired">
                    <div class="status-bar-left">
                        @if (!aiAvailable) {
                            <span class="ai-badge ai-off">
                                <mat-icon>smart_toy</mat-icon> AI unavailable — map manually
                            </span>
                        } @else {
                            <span class="ai-badge ai-on">
                                <mat-icon>auto_awesome</mat-icon> AI-assisted
                            </span>
                        }
                    </div>
                    <div class="status-bar-right">
                        @if (sessionExpired) {
                            <span class="countdown expired">Session expired — re-upload required</span>
                        } @else {
                            <mat-icon class="clock-icon">timer</mat-icon>
                            <span class="countdown" [class.countdown-warn]="sessionExpiringSoon">
                                {{ countdownDisplay }} remaining
                            </span>
                        }
                    </div>
                </div>

                <!-- Warnings from backend -->
                @for (w of warnings; track w) {
                    <div class="alert alert-warn">
                        <mat-icon>warning_amber</mat-icon> {{ w }}
                    </div>
                }

                <!-- Required fields not yet mapped -->
                @if (requiredUnmapped.length > 0) {
                    <div class="alert alert-warn">
                        <mat-icon>assignment_late</mat-icon>
                        Required fields not mapped yet:
                        <strong>{{ requiredUnmappedLabels }}</strong>
                    </div>
                }

                @if (mappingError) {
                    <div class="alert alert-error">
                        <mat-icon>error_outline</mat-icon> {{ mappingError }}
                    </div>
                }

                <!-- Mapping table -->
                <div class="mapping-table">
                    <div class="mapping-header">
                        <span>CSV Column</span>
                        <span>Maps To</span>
                        <span class="col-conf">Confidence</span>
                    </div>

                    @for (h of headers; track h) {
                        <div class="mapping-row"
                            [class.row-duplicate]="isTargetDuplicate(columnMapping[h])"
                            [class.row-unmapped]="!columnMapping[h]">
                            <div class="source-col">
                                <span class="source-name">{{ h }}</span>
                            </div>

                            <div class="target-select-wrap">
                                <div class="select-wrapper"
                                    [class.select-mapped]="columnMapping[h]"
                                    [class.select-dup]="isTargetDuplicate(columnMapping[h])"
                                >
                                    <select class="custom-select" (change)="onMappingChange(h, $event)">
                                        <option value="">— ignore this column —</option>
                                        @for (tf of targetFields; track tf.key) {
                                            <option [value]="tf.key" [selected]="columnMapping[h] === tf.key">
                                                {{ tf.displayName }}{{ tf.required ? ' *' : '' }}
                                            </option>
                                        }
                                    </select>
                                    <div class="select-arrow">
                                        <svg width="12" height="8" viewBox="0 0 12 8" fill="none">
                                            <path d="M1 1L6 6L11 1" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                                        </svg>
                                    </div>
                                </div>
                                @if (isTargetDuplicate(columnMapping[h])) {
                                    <span class="dup-label">Already mapped</span>
                                }
                            </div>

                            <div class="conf-cell">
                                @if (getSuggestionForHeader(h); as s) {
                                    <span
                                        class="conf-badge"
                                        [class.conf-high]="s.confidence >= 0.8"
                                        [class.conf-mid]="s.confidence >= 0.5 && s.confidence < 0.8"
                                        [class.conf-low]="s.confidence < 0.5"
                                        [matTooltip]="getConfidenceTooltip(s)"
                                        matTooltipPosition="left"
                                        matTooltipClass="multiline-tooltip"
                                    >
                                        {{ (s.confidence * 100).toFixed(0) }}%
                                        <mat-icon class="conf-info">info</mat-icon>
                                    </span>
                                } @else {
                                    <span class="conf-none">—</span>
                                }
                            </div>
                        </div>
                    }
                </div>

                <!-- Required field status chips -->
                <div class="req-status-row">
                    @for (f of requiredFieldStatus; track f.key) {
                        <span class="req-chip" [class.req-ok]="f.sourceColumn" [class.req-missing]="!f.sourceColumn">
                            <mat-icon>{{ f.sourceColumn ? 'check_circle' : 'cancel' }}</mat-icon>
                            <span class="req-label">{{ f.displayName }}</span>
                            @if (f.sourceColumn) {
                                <span class="req-src">← {{ f.sourceColumn }}</span>
                            } @else {
                                <span class="req-hint">not mapped</span>
                            }
                        </span>
                    }
                </div>

                <mat-divider class="section-divider"></mat-divider>

                <!-- Live preview -->
                <h3 class="section-label">
                    <mat-icon>preview</mat-icon>
                    Data Preview (first {{ previewRows.length }} rows)
                    @if (selectedSiteName) {
                        <span class="preview-badge site-badge">
                            <mat-icon>location_on</mat-icon>{{ selectedSiteName }}
                        </span>
                    }
                    @if (selectedGroupName) {
                        <span class="preview-badge group-badge">
                            <mat-icon>group</mat-icon>{{ selectedGroupName }}
                        </span>
                    }
                </h3>

                @if (previewColumns.length > 0) {
                    <div class="preview-wrap">
                        <table class="preview-table">
                            <thead>
                                <tr>
                                    @for (col of previewColumns; track col.header) {
                                        <th>{{ col.label }}</th>
                                    }
                                    @if (selectedSiteName) {
                                        <th class="injected-col">Site</th>
                                    }
                                    @if (selectedGroupName) {
                                        <th class="injected-col">Group</th>
                                    }
                                </tr>
                            </thead>
                            <tbody>
                                @for (row of livePreviewData; track $index) {
                                    <tr>
                                        @for (cell of row; track $index) {
                                            <td>{{ cell }}</td>
                                        }
                                        @if (selectedSiteName) {
                                            <td class="injected-col">{{ selectedSiteName }}</td>
                                        }
                                        @if (selectedGroupName) {
                                            <td class="injected-col">{{ selectedGroupName }}</td>
                                        }
                                    </tr>
                                }
                            </tbody>
                        </table>
                    </div>
                } @else {
                    <p class="preview-empty">Map at least one column above to see a preview.</p>
                }
            }

            <!-- ══════════════════════════════════════════════════════════════
                 PHASE: importing
                 ════════════════════════════════════════════════════════════ -->
            @if (phase === 'importing') {
                <div class="center-status">
                    <mat-progress-bar mode="indeterminate"></mat-progress-bar>
                    <p class="status-label">Importing users…</p>
                </div>
            }

            <!-- ══════════════════════════════════════════════════════════════
                 PHASE: results
                 ════════════════════════════════════════════════════════════ -->
            @if (phase === 'results') {
                @if (importError) {
                    <div class="alert alert-error">
                        <mat-icon>error_outline</mat-icon> {{ importError }}
                    </div>
                }

                @if (importResult) {
                    <div class="done-header">
                        <mat-icon class="done-icon">task_alt</mat-icon>
                        <h3 class="done-title">Import Complete</h3>
                    </div>

                    <div class="summary-chips">
                        <span class="chip chip-total">
                            <mat-icon>list</mat-icon> {{ importResult.total_processed }} processed
                        </span>
                        <span class="chip chip-ok">
                            <mat-icon>how_to_reg</mat-icon> {{ importResult.total_registered }} registered
                        </span>
                        @if (importResult.total_skipped > 0) {
                            <span class="chip chip-skip">
                                <mat-icon>skip_next</mat-icon> {{ importResult.total_skipped }} skipped
                            </span>
                        }
                        @if (importResult.total_failed > 0) {
                            <span class="chip chip-fail">
                                <mat-icon>error</mat-icon> {{ importResult.total_failed }} failed
                            </span>
                        }
                    </div>

                    @if ((importResult.failed_records?.length ?? 0) > 0) {
                        <h3 class="section-label warn-label">
                            <mat-icon>report_problem</mat-icon> Failed records
                        </h3>
                        <div class="results-table-wrap">
                            <table class="results-table">
                                <thead>
                                    <tr>
                                        <th>Row</th>
                                        <th>ID / Passport</th>
                                        <th>Email</th>
                                        <th>Errors</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    @for (r of (importResult.failed_records ?? []); track r.row) {
                                        <tr class="error-row">
                                            <td>{{ r.row }}</td>
                                            <td>{{ r.identity_number || '—' }}</td>
                                            <td>{{ r.email || '—' }}</td>
                                            <td class="reason-cell">{{ r.errors.join('; ') }}</td>
                                        </tr>
                                    }
                                </tbody>
                            </table>
                        </div>

                        @if (importResult.error_report_csv) {
                            <button mat-stroked-button color="accent" class="download-btn" (click)="downloadErrorReport()">
                                <mat-icon>download</mat-icon> Download Error Report (CSV)
                            </button>
                        }
                    }

                    @if (importResult.total_failed === 0 && importResult.total_skipped === 0) {
                        <p class="all-ok-msg">
                            <mat-icon>celebration</mat-icon>
                            All records were registered successfully!
                        </p>
                    }
                }
            }

            <!-- ══════════════════════════════════════════════════════════════
                 PHASE: audit
                 ════════════════════════════════════════════════════════════ -->
            @if (phase === 'audit') {
                <h3 class="section-label">
                    <mat-icon>history</mat-icon> Import History
                </h3>

                @if (auditLoading) {
                    <div class="center-status">
                        <mat-progress-bar mode="indeterminate"></mat-progress-bar>
                        <p class="status-label">Loading…</p>
                    </div>
                }

                @if (auditError) {
                    <div class="alert alert-error">
                        <mat-icon>error_outline</mat-icon> {{ auditError }}
                    </div>
                }

                @if (!auditLoading && auditRecords.length === 0 && !auditError) {
                    <p class="preview-empty">No import history found.</p>
                }

                @if (auditRecords.length > 0) {
                    <div class="results-table-wrap">
                        <table class="results-table">
                            <thead>
                                <tr>
                                    <th>Date</th>
                                    <th>Columns</th>
                                    <th>AI</th>
                                    <th>Processed</th>
                                    <th>Registered</th>
                                    <th>Skipped</th>
                                    <th>Failed</th>
                                </tr>
                            </thead>
                            <tbody>
                                @for (r of auditRecords; track r.id) {
                                    <tr>
                                        <td class="audit-date">{{ formatDate(r.created_at) }}</td>
                                        <td class="audit-cols">{{ r.original_headers.join(', ') }}</td>
                                        <td>
                                            @if (r.ai_available) {
                                                <mat-icon class="ai-check">auto_awesome</mat-icon>
                                            } @else {
                                                <span class="text-dim">—</span>
                                            }
                                        </td>
                                        <td>{{ r.total_processed }}</td>
                                        <td class="text-ok">{{ r.total_registered }}</td>
                                        <td class="text-warn">{{ r.total_skipped }}</td>
                                        <td class="text-err">{{ r.total_failed }}</td>
                                    </tr>
                                }
                            </tbody>
                        </table>
                    </div>
                    <p class="audit-total">Showing {{ auditRecords.length }} of {{ auditTotal }} imports</p>
                }
            }

        </mat-dialog-content>

        <!-- ═══ ACTIONS ═══ -->
        <mat-dialog-actions align="end" class="dialog-actions">

            @if (phase === 'upload') {
                <button mat-button mat-dialog-close>Cancel</button>
                <button mat-raised-button color="primary"
                    [disabled]="!selectedFile || !!uploadError"
                    (click)="uploadAndAnalyze()">
                    <mat-icon>auto_awesome</mat-icon> Analyse & Map Columns
                </button>
            }

            @if (phase === 'mapping') {
                <button mat-button (click)="resetToUpload()">
                    <mat-icon>arrow_back</mat-icon> Re-upload
                </button>
                <button mat-raised-button color="primary"
                    [disabled]="!canImport"
                    (click)="confirmAndImport()">
                    <mat-icon>upload</mat-icon> Confirm & Import
                </button>
            }

            @if (phase === 'results') {
                <button mat-button (click)="resetToUpload()">
                    <mat-icon>upload_file</mat-icon> Import Another File
                </button>
                <button mat-raised-button color="primary"
                    (click)="dialogRef.close({ anyRegistered: !!(importResult && importResult.total_registered > 0), totalRegistered: importResult?.total_registered ?? 0 })">
                    <mat-icon>done</mat-icon> Close
                </button>
            }

            @if (phase === 'audit') {
                <button mat-button (click)="phase = 'upload'">
                    <mat-icon>arrow_back</mat-icon> Back
                </button>
                <button mat-raised-button color="primary" mat-dialog-close>
                    Close
                </button>
            }

        </mat-dialog-actions>
    `,
    styles: [`
        /* ── Dialog shell ──────────────────────────────────────────────── */
        .dialog-title {
            display: flex;
            align-items: center;
            gap: 10px;
            font-size: 20px;
            font-weight: 600;
            color: #1e293b;
        }
        .title-icon { color: var(--primary-color, #1e3a8a); font-size: 24px; }
        .close-btn {
            margin-left: auto;
            background: none;
            border: none;
            cursor: pointer;
            color: #64748b;
            display: flex;
            align-items: center;
            justify-content: center;
            width: 32px;
            height: 32px;
            border-radius: 6px;
            padding: 0;
            transition: background 0.15s, color 0.15s;
        }
        .close-btn:hover:not(:disabled) { background: #f1f5f9; color: #1e293b; }
        .close-btn:disabled { opacity: 0.35; cursor: not-allowed; }
        :host-context(.dark) .close-btn { color: #8b949e; }
        :host-context(.dark) .close-btn:hover:not(:disabled) { background: #21262d; color: #e6edf3; }

        .dialog-content {
            min-width: min(720px, 94vw);
            max-width: 860px;
            padding: 8px 24px 0;
            overflow-x: hidden;
        }

        .phase-description {
            font-size: 14px;
            color: #64748b;
            margin: 0 0 16px;
            line-height: 1.5;
        }

        /* ── Template bar ──────────────────────────────────────────────── */
        .template-bar {
            display: flex;
            align-items: center;
            gap: 10px;
            background: #f0f4ff;
            border: 1px solid #c7d7fb;
            border-radius: 8px;
            padding: 10px 16px;
            margin-bottom: 18px;
            font-size: 14px;
            color: #374151;
            flex-wrap: wrap;
        }
        .template-icon { color: var(--primary-color, #1e3a8a); font-size: 20px; }
        .template-btn  { margin-left: auto; font-size: 13px; height: 34px; }
        .audit-btn     { font-size: 13px; height: 34px; display: inline-flex; align-items: center; gap: 4px; }

        /* ── Drop zone ─────────────────────────────────────────────────── */
        .drop-zone {
            border: 2px dashed #94a3b8;
            border-radius: 12px;
            padding: 36px 24px;
            text-align: center;
            cursor: pointer;
            transition: border-color 0.2s, background 0.2s;
            background: #f8fafc;
            margin-bottom: 12px;
        }
        .drop-zone:hover, .drop-zone.drag-over { border-color: var(--primary-color, #1e3a8a); background: #eff3ff; }
        .drop-zone.has-file { border-color: #16a34a; background: #f0fdf4; }
        .drop-icon { font-size: 48px; width: 48px; height: 48px; color: #94a3b8; margin-bottom: 10px; }
        .drop-icon.file-ready { color: #16a34a; }
        .drop-label { font-size: 15px; color: #334155; margin-bottom: 6px; }
        .file-name-display { font-weight: 500; color: #16a34a; word-break: break-all; }
        .file-size { font-size: 12px; color: #64748b; }
        .drop-hint { font-size: 12px; color: #94a3b8; }

        /* ── Site / group assignment ───────────────────────────────────── */
        .assign-section {
            background: #f8fafc;
            border: 1px solid #e2e8f0;
            border-radius: 10px;
            padding: 14px 16px;
            margin-bottom: 12px;
        }
        .assign-label {
            display: flex;
            align-items: center;
            gap: 6px;
            font-size: 13px;
            font-weight: 600;
            color: #374151;
            margin: 0 0 12px;
        }
        .assign-icon { font-size: 16px; width: 16px; height: 16px; color: var(--primary-color, #1e3a8a); }
        .assign-row {
            display: grid;
            grid-template-columns: 1fr 1fr;
            gap: 12px;
            margin-bottom: 10px;
        }
        .assign-field { display: flex; flex-direction: column; gap: 4px; }
        .field-label { font-size: 12px; font-weight: 600; color: #64748b; text-transform: uppercase; letter-spacing: 0.4px; }
        .field-hint  { font-size: 11px; color: #94a3b8; }
        .assign-note {
            display: flex;
            align-items: flex-start;
            gap: 5px;
            font-size: 12px;
            color: #64748b;
            margin: 0;
            line-height: 1.5;
        }
        .note-icon { font-size: 14px; width: 14px; height: 14px; flex-shrink: 0; margin-top: 1px; color: #94a3b8; }

        :host-context(.dark) .assign-section { background: #1c2128; border-color: #30363d; }
        :host-context(.dark) .assign-label { color: #e6edf3; }
        :host-context(.dark) .field-label { color: #8b949e; }
        :host-context(.dark) .field-hint { color: #4b5563; }
        :host-context(.dark) .assign-note { color: #8b949e; }

        /* ── Alerts ────────────────────────────────────────────────────── */
        .alert {
            display: flex;
            align-items: flex-start;
            gap: 8px;
            border-radius: 8px;
            padding: 10px 14px;
            font-size: 13px;
            margin-bottom: 10px;
        }
        .alert mat-icon { font-size: 18px; width: 18px; height: 18px; flex-shrink: 0; margin-top: 1px; }
        .alert-error { background: #fef2f2; border: 1px solid #fecaca; color: #dc2626; }
        .alert-warn  { background: #fffbeb; border: 1px solid #fde68a; color: #92400e; }

        /* ── Status bar ────────────────────────────────────────────────── */
        .status-bar {
            display: flex;
            align-items: center;
            justify-content: space-between;
            padding: 8px 14px;
            border-radius: 8px;
            background: #f8fafc;
            border: 1px solid #e2e8f0;
            margin-bottom: 12px;
            gap: 12px;
            flex-wrap: wrap;
        }
        .status-bar-warning { background: #fffbeb; border-color: #fde68a; }
        .status-bar-error   { background: #fef2f2; border-color: #fecaca; }
        .status-bar-left, .status-bar-right { display: flex; align-items: center; gap: 6px; }

        .ai-badge {
            display: inline-flex;
            align-items: center;
            gap: 4px;
            font-size: 12px;
            font-weight: 600;
            padding: 3px 10px;
            border-radius: 12px;
        }
        .ai-badge mat-icon { font-size: 14px; width: 14px; height: 14px; }
        .ai-on  { background: #ede9fe; color: #6d28d9; }
        .ai-off { background: #f1f5f9; color: #64748b; }

        .clock-icon { font-size: 16px; width: 16px; height: 16px; color: #64748b; }
        .countdown { font-size: 13px; font-weight: 600; color: #374151; font-variant-numeric: tabular-nums; }
        .countdown-warn { color: #dc2626; }
        .countdown.expired { color: #dc2626; }

        /* ── Mapping table ─────────────────────────────────────────────── */
        .mapping-table {
            border: 1px solid #e2e8f0;
            border-radius: 10px;
            overflow: hidden;
            margin-bottom: 4px;
        }
        .mapping-header {
            display: grid;
            grid-template-columns: 180px 1fr 110px;
            gap: 12px;
            padding: 9px 16px;
            background: #f1f5f9;
            font-size: 12px;
            font-weight: 700;
            color: #64748b;
            text-transform: uppercase;
            letter-spacing: 0.4px;
        }
        .col-conf { text-align: center; }

        .mapping-row {
            display: grid;
            grid-template-columns: 180px 1fr 110px;
            gap: 12px;
            align-items: center;
            padding: 8px 16px;
            border-top: 1px solid #f1f5f9;
            transition: background 0.15s;
        }
        .mapping-row:hover  { background: #f8fafc; }
        .row-duplicate      { background: #fff7ed !important; }
        .row-unmapped       { opacity: 0.55; }
        .row-unmapped:hover { opacity: 1; background: #f8fafc; }

        /* ── Required field status chips ───────────────────────────────── */
        .req-status-row {
            display: flex; flex-wrap: wrap; gap: 8px;
            margin: 10px 0 4px;
        }
        .req-chip {
            display: inline-flex; align-items: center; gap: 4px;
            font-size: 12px; font-weight: 500; padding: 4px 10px 4px 7px;
            border-radius: 20px; border: 1px solid transparent;
        }
        .req-chip mat-icon { font-size: 15px; width: 15px; height: 15px; }
        .req-ok      { background: #dcfce7; color: #15803d; border-color: #bbf7d0; }
        .req-missing { background: #fee2e2; color: #dc2626; border-color: #fecaca; }
        .req-src  { font-size: 11px; opacity: 0.75; margin-left: 2px; }
        .req-hint { font-size: 11px; font-style: italic; opacity: 0.85; margin-left: 2px; }

        .source-col { display: flex; align-items: center; }
        .source-name {
            font-size: 13px;
            font-weight: 600;
            color: #374151;
            font-family: monospace;
            background: #f1f5f9;
            padding: 2px 8px;
            border-radius: 4px;
        }

        /* ── Custom select ─────────────────────────────────────────────── */
        .target-select-wrap { display: flex; flex-direction: column; gap: 2px; }
        .select-wrapper { position: relative; }
        .custom-select {
            width: 100%;
            padding: 8px 32px 8px 12px;
            border: 1px solid #d1d5db;
            border-radius: 8px;
            background: #fff;
            color: #374151;
            font-size: 13px;
            font-family: inherit;
            appearance: none;
            -webkit-appearance: none;
            cursor: pointer;
            outline: none;
            transition: border-color 0.2s, box-shadow 0.2s;
        }
        .custom-select:focus { border-color: var(--primary-color, #1e3a8a); box-shadow: 0 0 0 3px rgba(59,130,246,0.15); }
        .select-mapped .custom-select { border-color: #16a34a; color: #15803d; }
        .select-dup    .custom-select { border-color: #f59e0b; }
        .select-arrow { position: absolute; right: 10px; top: 50%; transform: translateY(-50%); pointer-events: none; color: #6b7280; }
        .dup-label { font-size: 11px; color: #b45309; font-weight: 500; }

        /* ── Confidence badges ─────────────────────────────────────────── */
        .conf-cell { display: flex; justify-content: center; }
        .conf-badge {
            display: inline-flex;
            align-items: center;
            gap: 3px;
            font-size: 12px;
            font-weight: 700;
            padding: 3px 10px;
            border-radius: 10px;
            cursor: default;
        }
        .conf-info { font-size: 13px !important; width: 13px !important; height: 13px !important; opacity: 0.7; }
        .conf-high { background: #dcfce7; color: #15803d; }
        .conf-mid  { background: #fef9c3; color: #a16207; }
        .conf-low  { background: #fee2e2; color: #b91c1c; }
        .conf-none { font-size: 13px; color: #94a3b8; }

        /* ── Section ───────────────────────────────────────────────────── */
        .section-divider { margin: 14px 0 12px; }
        .section-label {
            display: flex;
            align-items: center;
            gap: 6px;
            font-size: 14px;
            font-weight: 600;
            color: #374151;
            margin: 0 0 10px;
        }
        .section-label mat-icon { font-size: 18px; width: 18px; height: 18px; }
        .warn-label { color: #b45309; }

        /* ── Preview context badges ────────────────────────────────────── */
        .preview-badge {
            display: inline-flex; align-items: center; gap: 3px;
            font-size: 11px; font-weight: 500; padding: 2px 8px 2px 5px;
            border-radius: 12px; margin-left: 8px; vertical-align: middle;
        }
        .preview-badge mat-icon { font-size: 13px; width: 13px; height: 13px; }
        .site-badge  { background: #dbeafe; color: #1d4ed8; }
        .group-badge { background: #dcfce7; color: #15803d; }

        /* ── Preview table ─────────────────────────────────────────────── */
        .preview-wrap { overflow-x: auto; border-radius: 8px; border: 1px solid #e2e8f0; margin-bottom: 4px; }
        .preview-table { width: 100%; border-collapse: collapse; font-size: 13px; }
        .preview-table th { background: #f1f5f9; color: #475569; padding: 8px 12px; text-align: left; font-weight: 600; white-space: nowrap; }
        .preview-table td { padding: 7px 12px; border-top: 1px solid #f1f5f9; color: #374151; max-width: 180px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
        .preview-table th.injected-col { background: #eff6ff; color: #1d4ed8; }
        .preview-table td.injected-col { color: #1d4ed8; font-style: italic; }
        .preview-empty { font-size: 13px; color: #94a3b8; text-align: center; padding: 12px 0; margin: 0; }

        /* ── Center states ─────────────────────────────────────────────── */
        .center-status { display: flex; flex-direction: column; align-items: stretch; padding: 24px 0; }
        .status-label  { font-size: 14px; color: #64748b; margin: 12px 0 0; text-align: center; }

        /* ── Summary chips ─────────────────────────────────────────────── */
        .summary-chips { display: flex; flex-wrap: wrap; gap: 10px; margin-bottom: 16px; }
        .chip {
            display: inline-flex;
            align-items: center;
            gap: 5px;
            padding: 5px 14px;
            border-radius: 20px;
            font-size: 13px;
            font-weight: 500;
        }
        .chip mat-icon { font-size: 16px; width: 16px; height: 16px; }
        .chip-total { background: #e2e8f0; color: #334155; }
        .chip-ok    { background: #dcfce7; color: #15803d; }
        .chip-skip  { background: #fef9c3; color: #a16207; }
        .chip-fail  { background: #fee2e2; color: #b91c1c; }

        /* ── Done header ───────────────────────────────────────────────── */
        .done-header { display: flex; align-items: center; gap: 12px; margin-bottom: 16px; }
        .done-icon   { font-size: 36px; width: 36px; height: 36px; color: #16a34a; }
        .done-title  { font-size: 18px; font-weight: 600; color: #1e293b; margin: 0; }
        .all-ok-msg  { display: flex; align-items: center; gap: 8px; color: #15803d; font-size: 14px; font-weight: 500; margin: 8px 0; }
        .all-ok-msg mat-icon { color: #22c55e; }

        /* ── Results / audit table ─────────────────────────────────────── */
        .results-table-wrap { overflow-x: auto; border-radius: 8px; border: 1px solid #e2e8f0; margin-bottom: 10px; }
        .results-table { width: 100%; border-collapse: collapse; font-size: 13px; }
        .results-table th { background: #f1f5f9; color: #475569; padding: 8px 12px; text-align: left; font-weight: 600; white-space: nowrap; }
        .results-table td { padding: 7px 12px; border-top: 1px solid #f1f5f9; color: #374151; vertical-align: top; }
        .error-row td { background: #fff5f5; }
        .reason-cell { color: #dc2626; max-width: 260px; word-break: break-word; white-space: normal; }

        .audit-date { white-space: nowrap; font-size: 12px; color: #64748b; }
        .audit-cols { max-width: 200px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; font-size: 12px; font-family: monospace; }
        .audit-total { font-size: 12px; color: #94a3b8; margin: 4px 0 0; }
        .ai-check { font-size: 16px !important; width: 16px !important; height: 16px !important; color: #7c3aed; }
        .text-ok   { color: #15803d; font-weight: 600; }
        .text-warn { color: #a16207; font-weight: 600; }
        .text-err  { color: #b91c1c; font-weight: 600; }
        .text-dim  { color: #94a3b8; }

        .download-btn { display: inline-flex; align-items: center; gap: 6px; font-size: 13px; margin-bottom: 8px; }

        /* ── Dialog actions ────────────────────────────────────────────── */
        .dialog-actions { padding: 12px 24px; gap: 10px; }

        /* ── Dark mode ─────────────────────────────────────────────────── */
        :host-context(.dark) .dialog-title  { color: #e6edf3; }
        :host-context(.dark) .phase-description { color: #8b949e; }

        :host-context(.dark) .template-bar { background: rgba(30,58,138,0.2); border-color: #3d4f6e; color: #e6edf3; }
        :host-context(.dark) .drop-zone { background: #1e2843; border-color: #3d4f6e; }
        :host-context(.dark) .drop-zone:hover, :host-context(.dark) .drop-zone.drag-over { background: rgba(59,130,246,0.1); border-color: #3b82f6; }
        :host-context(.dark) .drop-zone.has-file { background: rgba(22,163,74,0.1); border-color: #16a34a; }
        :host-context(.dark) .drop-icon { color: #8b949e; }
        :host-context(.dark) .drop-label { color: #e6edf3; }
        :host-context(.dark) .drop-hint, :host-context(.dark) .file-size { color: #6b7280; }

        :host-context(.dark) .alert-error { background: rgba(220,38,38,0.15); border-color: rgba(220,38,38,0.3); color: #f87171; }
        :host-context(.dark) .alert-warn  { background: rgba(234,179,8,0.1); border-color: rgba(234,179,8,0.3); color: #fbbf24; }

        :host-context(.dark) .status-bar { background: #1c2128; border-color: #30363d; }
        :host-context(.dark) .status-bar-warning { background: rgba(234,179,8,0.1); border-color: rgba(234,179,8,0.3); }
        :host-context(.dark) .status-bar-error   { background: rgba(220,38,38,0.1); border-color: rgba(220,38,38,0.3); }
        :host-context(.dark) .ai-on  { background: rgba(109,40,217,0.2); color: #a78bfa; }
        :host-context(.dark) .ai-off { background: #21262d; color: #6b7280; }
        :host-context(.dark) .countdown { color: #e6edf3; }
        :host-context(.dark) .clock-icon { color: #8b949e; }

        :host-context(.dark) .mapping-table { border-color: #21262d; }
        :host-context(.dark) .mapping-header { background: #161b22; color: #8b949e; }
        :host-context(.dark) .mapping-row { border-top-color: #21262d; }
        :host-context(.dark) .mapping-row:hover { background: #1c2128; }
        :host-context(.dark) .row-duplicate { background: rgba(245,158,11,0.08) !important; }
        :host-context(.dark) .row-unmapped:hover { background: #1c2128; }
        :host-context(.dark) .req-ok      { background: #14532d; color: #86efac; border-color: #166534; }
        :host-context(.dark) .req-missing { background: #450a0a; color: #fca5a5; border-color: #7f1d1d; }
        :host-context(.dark) .source-name { background: #21262d; color: #79c0ff; }

        :host-context(.dark) .custom-select { background: #1c2128; border-color: #30363d; color: #e6edf3; }
        :host-context(.dark) .custom-select:focus { border-color: #3b82f6; box-shadow: 0 0 0 3px rgba(59,130,246,0.2); }
        :host-context(.dark) .select-mapped .custom-select { border-color: #16a34a; color: #34d399; }
        :host-context(.dark) .select-dup .custom-select { border-color: #f59e0b; }
        :host-context(.dark) .select-arrow { color: #8b949e; }
        :host-context(.dark) .dup-label { color: #fbbf24; }

        :host-context(.dark) .conf-high { background: rgba(22,163,74,0.2); color: #34d399; }
        :host-context(.dark) .conf-mid  { background: rgba(161,98,7,0.2); color: #fbbf24; }
        :host-context(.dark) .conf-low  { background: rgba(185,28,28,0.2); color: #f87171; }
        :host-context(.dark) .conf-none { color: #4b5563; }

        :host-context(.dark) .section-label { color: #e6edf3; }
        :host-context(.dark) .warn-label { color: #fbbf24; }

        :host-context(.dark) .preview-wrap { border-color: #21262d; }
        :host-context(.dark) .preview-table th { background: #1c2128; color: #8b949e; }
        :host-context(.dark) .preview-table td { color: #e6edf3; border-top-color: #21262d; }
        :host-context(.dark) .preview-empty { color: #4b5563; }
        :host-context(.dark) .site-badge  { background: #1e3a5f; color: #93c5fd; }
        :host-context(.dark) .group-badge { background: #14532d; color: #86efac; }
        :host-context(.dark) .preview-table th.injected-col { background: #1e3a5f; color: #93c5fd; }
        :host-context(.dark) .preview-table td.injected-col { color: #93c5fd; }
        :host-context(.dark) .status-label { color: #8b949e; }

        :host-context(.dark) .chip-total { background: rgba(255,255,255,0.08); color: #e6edf3; }
        :host-context(.dark) .chip-ok    { background: rgba(22,163,74,0.15); color: #34d399; }
        :host-context(.dark) .chip-skip  { background: rgba(161,98,7,0.15); color: #fbbf24; }
        :host-context(.dark) .chip-fail  { background: rgba(185,28,28,0.15); color: #f87171; }

        :host-context(.dark) .done-title { color: #e6edf3; }
        :host-context(.dark) .results-table-wrap { border-color: #21262d; }
        :host-context(.dark) .results-table th { background: #1c2128; color: #8b949e; }
        :host-context(.dark) .results-table td { color: #e6edf3; border-top-color: #21262d; }
        :host-context(.dark) .error-row td { background: rgba(220,38,38,0.08); }
        :host-context(.dark) .reason-cell { color: #f87171; }
        :host-context(.dark) .audit-date, :host-context(.dark) .audit-total { color: #6b7280; }
        :host-context(.dark) .audit-cols { color: #79c0ff; }
    `],
})
export class BulkUserRegistrationDialogComponent implements OnInit, OnDestroy {

    // ─── State ───────────────────────────────────────────────────────────────
    phase: DialogPhase = "upload";

    // Upload
    isDragOver    = false;
    selectedFile: File | null = null;
    uploadError: string | null = null;

    // Site / group selection (optional, shown on upload phase)
    sites:           { id: number; name: string }[] = [];
    groups:          { id: number; name: string }[] = [];
    selectedSiteId:  number | null = null;
    selectedGroupId: number | null = null;
    sitesLoading  = false;
    groupsLoading = false;

    // Analyze response
    sessionId: string | null = null;
    expiresAt: Date | null = null;
    headers: string[] = [];
    suggestions: Suggestion[] = [];
    previewRows: string[][] = [];
    aiAvailable = true;
    warnings: string[] = [];

    // Mapping
    columnMapping: Record<string, string> = {}; // source_column → target_field | ""
    mappingError: string | null = null;
    sessionExpired = false;
    countdownDisplay = "";
    private countdownInterval: ReturnType<typeof setInterval> | null = null;

    // Results
    importResult: ImportResponse | null = null;
    importError: string | null = null;

    // Audit
    auditRecords: AuditRecord[] = [];
    auditTotal   = 0;
    auditLoading = false;
    auditError: string | null = null;

    readonly targetFields = TARGET_FIELDS;

    // ─── Constructor ─────────────────────────────────────────────────────────
    constructor(
        public dialogRef: MatDialogRef<BulkUserRegistrationDialogComponent>,
        private http: HttpClient,
        private siteService: SiteService,
        private groupService: GroupService,
        private currentSiteService: CurrentSiteService,
        @Inject(MAT_DIALOG_DATA) public data: { siteId?: number | null; groupId?: number | null }
    ) {
        this.dialogRef.disableClose = false;
    }

    ngOnInit(): void {
        const preselectedSiteId = this.data?.siteId ?? null;
        const preselectedGroupId = this.data?.groupId ?? null;

        if (preselectedSiteId) {
            this.selectedSiteId = preselectedSiteId;
            this.selectedGroupId = preselectedGroupId;
        } else {
            const current = this.currentSiteService.getSite();
            if (current?.id) this.selectedSiteId = current.id;
        }

        this.sitesLoading = true;
        this.siteService.getSites().subscribe({
            next: (sites) => {
                this.sites = sites;
                this.sitesLoading = false;
                if (this.selectedSiteId) this.loadGroups(this.selectedSiteId);
            },
            error: () => { this.sitesLoading = false; },
        });
    }

    ngOnDestroy(): void {
        this.stopCountdown();
    }

    // ─── Site / group helpers ─────────────────────────────────────────────────
    onSiteChange(event: Event): void {
        const val = (event.target as HTMLSelectElement).value;
        this.selectedSiteId  = val ? +val : null;
        this.selectedGroupId = null;
        this.groups = [];
        if (this.selectedSiteId) this.loadGroups(this.selectedSiteId);
    }

    onGroupChange(event: Event): void {
        const val = (event.target as HTMLSelectElement).value;
        this.selectedGroupId = val ? +val : null;
    }

    private loadGroups(siteId: number): void {
        this.groupsLoading = true;
        const preselectedGroupId = this.data?.groupId ?? null;
        this.groupService.getGroups(siteId).subscribe({
            next: (groups) => {
                this.groups = groups;
                if (preselectedGroupId && groups.some(g => g.id === preselectedGroupId)) {
                    this.selectedGroupId = preselectedGroupId;
                }
                this.groupsLoading = false;
            },
            error: () => { this.groupsLoading = false; },
        });
    }

    // ─── Computed ─────────────────────────────────────────────────────────────

    get requiredUnmapped(): string[] {
        const mapped = new Set(Object.values(this.columnMapping).filter(v => v));
        return REQUIRED_KEYS.filter(k => !mapped.has(k));
    }

    get requiredUnmappedLabels(): string {
        return this.requiredUnmapped
            .map(k => TARGET_FIELDS.find(f => f.key === k)?.displayName ?? k)
            .join(", ");
    }

    /** Status of every required field: mapped → which CSV column; unmapped → null. */
    get requiredFieldStatus(): { key: string; displayName: string; sourceColumn: string | null }[] {
        const sourceFor: Record<string, string> = {};
        for (const [src, target] of Object.entries(this.columnMapping)) {
            if (target) sourceFor[target] = src;
        }
        return TARGET_FIELDS
            .filter(f => f.required)
            .map(f => ({ key: f.key, displayName: f.displayName, sourceColumn: sourceFor[f.key] ?? null }));
    }

    get duplicateTargets(): Set<string> {
        const counts: Record<string, number> = {};
        Object.values(this.columnMapping).forEach(v => {
            if (v) counts[v] = (counts[v] || 0) + 1;
        });
        return new Set(
            Object.entries(counts).filter(([, c]) => c > 1).map(([k]) => k)
        );
    }

    get canImport(): boolean {
        return this.requiredUnmapped.length === 0 &&
               this.duplicateTargets.size === 0 &&
               !this.sessionExpired;
    }

    get sessionExpiringSoon(): boolean {
        if (!this.expiresAt || this.sessionExpired) return false;
        return this.expiresAt.getTime() - Date.now() < 120_000;
    }

    getSuggestionForHeader(sourceCol: string): Suggestion | undefined {
        return this.suggestions.find(s => s.source_column === sourceCol);
    }

    getConfidenceTooltip(s: Suggestion): string {
        const pct = (s.confidence * 100).toFixed(0);
        const level = s.confidence >= 0.8 ? 'High confidence' : s.confidence >= 0.5 ? 'Medium confidence' : 'Low confidence';
        const hint = s.confidence >= 0.8
            ? 'AI is very confident this mapping is correct.'
            : s.confidence >= 0.5
            ? 'AI is moderately confident — review this mapping.'
            : 'AI is unsure about this mapping — please verify manually.';
        return `${level} (${pct}%)\n${hint}${s.reasoning ? '\n\n' + s.reasoning : ''}`;
    }

    isTargetDuplicate(targetField: string): boolean {
        return !!targetField && this.duplicateTargets.has(targetField);
    }

    /** Columns that have an active mapping — used to build the live preview. */
    get previewColumns(): { header: string; label: string; index: number; targetKey: string }[] {
        return this.headers
            .map((h, i) => ({ h, target: this.columnMapping[h], i }))
            .filter(c => c.target)
            .map(c => ({
                header:    c.h,
                label:     TARGET_FIELDS.find(f => f.key === c.target)?.displayName ?? c.target,
                index:     c.i,
                targetKey: c.target,
            }));
    }

    get livePreviewData(): string[][] {
        return this.previewRows.map(row =>
            this.previewColumns.map(col => {
                const raw = row[col.index] || "";
                if (!raw) return "—";
                return col.targetKey === "msisdn" ? this.normalizeMsisdn(raw) : raw;
            })
        );
    }

    get selectedSiteName(): string | null {
        return this.sites.find(s => s.id === this.selectedSiteId)?.name ?? null;
    }

    get selectedGroupName(): string | null {
        return this.groups.find(g => g.id === this.selectedGroupId)?.name ?? null;
    }

    /**
     * Normalize a phone number to digits-only with country code.
     *
     * Rules:
     *  1. Strip every non-digit character (+, -, spaces, brackets, dots)
     *  2. If the result starts with 0  → replace the leading 0 with "27"
     *     (local SA format: 0821234567 → 27821234567)
     *  3. Otherwise keep as-is — the country code is already present
     *     (e.g. +44-7867-431382 → 447867431382)
     */
    private normalizeMsisdn(raw: string): string {
        let digits = raw.replace(/\D/g, "");
        if (digits.startsWith("0")) {
            digits = "27" + digits.slice(1);
        }
        return digits;
    }

    // ─── File handling ────────────────────────────────────────────────────────
    onDragOver(e: DragEvent): void { e.preventDefault(); e.stopPropagation(); this.isDragOver = true; }
    onDragLeave(e: DragEvent): void { e.preventDefault(); e.stopPropagation(); this.isDragOver = false; }

    onDrop(e: DragEvent): void {
        e.preventDefault(); e.stopPropagation();
        this.isDragOver = false;
        this.handleFile(e.dataTransfer?.files?.[0] ?? null);
    }

    onFileSelected(e: Event): void {
        const input = e.target as HTMLInputElement;
        this.handleFile(input.files?.[0] ?? null);
        input.value = "";
    }

    private handleFile(file: File | null): void {
        this.uploadError  = null;
        this.selectedFile = null;
        if (!file) return;
        const name = file.name.toLowerCase();
        if (!name.endsWith(".csv") && !name.endsWith(".xlsx")) {
            this.uploadError = "Only CSV and Excel (.xlsx) files are accepted.";
            return;
        }
        if (file.size > MAX_FILE_SIZE_BYTES) {
            this.uploadError = `File too large (${this.formatFileSize(file.size)}). Maximum is 10 MB.`;
            return;
        }
        this.selectedFile = file;
    }

    // ─── Template download ────────────────────────────────────────────────────
    downloadTemplate(): void {
        const header  = "name,surname,identity_number,msisdn,email,gender,job_description";
        const example = "John,Doe,JD123456,27821234567,john@example.com,Male,Engineer";
        const blob    = new Blob([`${header}\n${example}\n`], { type: "text/csv;charset=utf-8;" });
        const url     = URL.createObjectURL(blob);
        const a       = document.createElement("a");
        a.href        = url;
        a.download    = "bulk_registration_template.csv";
        a.click();
        URL.revokeObjectURL(url);
    }

    // ─── Step 1: Upload & Analyse ─────────────────────────────────────────────
    uploadAndAnalyze(): void {
        if (!this.selectedFile) return;
        this.phase = "analyzing";
        this.uploadError = null;

        const form = new FormData();
        form.append("file", this.selectedFile);

        this.http
            .post<AnalyzeResponse>(
                `${environment.apiUrl}/api/v1/user/bulk-register/smart/analyze`,
                form
            )
            .subscribe({
                next: (res) => {
                    this.sessionId   = res.session_id;
                    this.expiresAt   = new Date(res.expires_at);
                    this.headers     = res.headers     ?? [];
                    this.suggestions = res.suggestions ?? [];
                    this.previewRows = res.preview_rows ?? [];
                    this.aiAvailable = res.ai_available ?? false;
                    this.warnings    = res.warnings    ?? [];

                    // Pre-fill columnMapping — default all to "" then apply suggestions.
                    // Ignore any suggestion whose target_field is not in TARGET_FIELDS
                    // (e.g. backend may suggest site_id / user_type — those are handled by the
                    // admin's site/group dropdowns and should NOT appear in the mapping table).
                    const validTargetKeys = new Set(TARGET_FIELDS.map(f => f.key));
                    this.columnMapping = {};
                    for (const h of this.headers) {
                        this.columnMapping[h] = "";
                    }
                    for (const s of this.suggestions) {
                        if (validTargetKeys.has(s.target_field)) {
                            this.columnMapping[s.source_column] = s.target_field;
                        }
                    }

                    this.startCountdown();
                    this.phase = "mapping";
                },
                error: (err) => {
                    this.uploadError = err?.error?.message ?? "Failed to analyse file. Please try again.";
                    this.phase = "upload";
                },
            });
    }

    // ─── Countdown ────────────────────────────────────────────────────────────
    private startCountdown(): void {
        this.stopCountdown();
        this.sessionExpired = false;
        this.updateCountdown();
        this.countdownInterval = setInterval(() => this.updateCountdown(), 1000);
    }

    private updateCountdown(): void {
        if (!this.expiresAt) return;
        const diff = this.expiresAt.getTime() - Date.now();
        if (diff <= 0) {
            this.sessionExpired    = true;
            this.countdownDisplay  = "00:00";
            this.stopCountdown();
        } else {
            const m = Math.floor(diff / 60_000);
            const s = Math.floor((diff % 60_000) / 1000);
            this.countdownDisplay = `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
        }
    }

    private stopCountdown(): void {
        if (this.countdownInterval !== null) {
            clearInterval(this.countdownInterval);
            this.countdownInterval = null;
        }
    }

    // ─── Mapping ──────────────────────────────────────────────────────────────
    onMappingChange(sourceCol: string, e: Event): void {
        const val = (e.target as HTMLSelectElement).value;
        this.columnMapping = { ...this.columnMapping, [sourceCol]: val };
        this.mappingError  = null;
    }

    // ─── Step 2: Confirm & Import ─────────────────────────────────────────────
    confirmAndImport(): void {
        this.mappingError = null;

        if (this.requiredUnmapped.length > 0) {
            this.mappingError = `Map all required fields first: ${this.requiredUnmappedLabels}`;
            return;
        }
        if (this.duplicateTargets.size > 0) {
            const labels = [...this.duplicateTargets]
                .map(k => TARGET_FIELDS.find(f => f.key === k)?.displayName ?? k);
            this.mappingError = `Each field can only be mapped once: ${labels.join(", ")}`;
            return;
        }

        this.phase = "importing";
        this.dialogRef.disableClose = true; // prevent accidental close mid-import
        this.stopCountdown();

        const payload: Record<string, unknown> = {
            session_id: this.sessionId,
            mappings: this.headers.map(h => ({
                source_column: h,
                target_field:  this.columnMapping[h] ?? "",
            })),
        };
        if (this.selectedSiteId)  payload["site_id"]  = this.selectedSiteId;
        if (this.selectedGroupId) payload["group_id"] = this.selectedGroupId;
        payload["user_type_id"] = 4; // Always register bulk users as "default" candidate role (ID 4)

        this.http
            .post<ImportResponse>(
                `${environment.apiUrl}/api/v1/user/bulk-register/smart/import`,
                payload
            )
            .subscribe({
                next: (res) => {
                    this.importResult = res;
                    this.importError  = null;
                    this.dialogRef.disableClose = false;
                    this.phase = "results";
                },
                error: (err) => {
                    const code = err?.error?.code as string | undefined;
                    const msg  = err?.error?.message ?? err?.message ?? "Import failed.";
                    this.dialogRef.disableClose = false;

                    switch (code) {
                        case "SESSION_NOT_FOUND":
                            this.sessionExpired = true;
                            this.phase = "mapping";
                            this.mappingError = "Session not found — please re-upload your file.";
                            break;
                        case "SESSION_EXPIRED":
                            this.sessionExpired = true;
                            this.phase = "mapping";
                            this.mappingError = "Session expired (15-min limit) — please re-upload your file.";
                            break;
                        case "MISSING_REQUIRED_FIELD":
                        case "DUPLICATE_TARGET_FIELD":
                        case "INVALID_SOURCE_COLUMN":
                            this.phase = "mapping";
                            this.mappingError = msg;
                            break;
                        case "FORBIDDEN":
                            this.importError = "Authentication error. Please log in again.";
                            this.phase = "results";
                            break;
                        default:
                            this.importError = msg;
                            this.phase = "results";
                    }
                },
            });
    }

    // ─── Step 3: Audit log ────────────────────────────────────────────────────
    openAudit(): void {
        this.phase = "audit";
        this.auditRecords = [];
        this.auditError   = null;
        this.auditLoading = true;

        this.http
            .get<{ records: AuditRecord[]; total: number; limit: number; offset: number }>(
                `${environment.apiUrl}/api/v1/user/bulk-register/smart/audit?limit=20&offset=0`
            )
            .subscribe({
                next: (res) => {
                    this.auditRecords = res.records;
                    this.auditTotal   = res.total;
                    this.auditLoading = false;
                },
                error: (err) => {
                    this.auditError   = err?.error?.message ?? "Failed to load import history.";
                    this.auditLoading = false;
                },
            });
    }

    // ─── Error report download ────────────────────────────────────────────────
    downloadErrorReport(): void {
        if (!this.importResult?.error_report_csv) return;
        const blob = new Blob([this.importResult.error_report_csv], { type: "text/csv;charset=utf-8;" });
        const url  = URL.createObjectURL(blob);
        const a    = document.createElement("a");
        a.href     = url;
        a.download = `import_errors_${new Date().toISOString().slice(0, 10)}.csv`;
        a.click();
        URL.revokeObjectURL(url);
    }

    // ─── Reset ────────────────────────────────────────────────────────────────
    resetToUpload(): void {
        this.stopCountdown();
        this.selectedFile     = null;
        this.uploadError      = null;
        this.sessionId        = null;
        this.expiresAt        = null;
        this.headers          = [];
        this.suggestions      = [];
        this.previewRows      = [];
        this.warnings         = [];
        this.columnMapping    = {};
        this.mappingError     = null;
        this.sessionExpired   = false;
        this.countdownDisplay = "";
        this.importResult     = null;
        this.importError      = null;
        // Keep site/group selection so user doesn't have to re-pick after re-uploading
        this.phase = "upload";
    }

    // ─── Utilities ────────────────────────────────────────────────────────────
    formatFileSize(bytes: number): string {
        if (bytes < 1024)        return `${bytes} B`;
        if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
        return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
    }

    formatDate(iso: string): string {
        return new Date(iso).toLocaleString();
    }
}
